import { useCallback, useEffect, useRef, useState } from "react";
import "@/App.css";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "sonner";
import { TopBar } from "@/deck/TopBar";
import { DeckNav } from "@/deck/DeckNav";
import { useTimer } from "@/deck/useTimer";
import { TOTAL_SECONDS, EASE, slideAt } from "@/deck/config";
import Cover from "@/slides/Cover";
import Problem from "@/slides/Problem";
import Solution from "@/slides/Solution";
import Product from "@/slides/Product";
import Impact from "@/slides/Impact";
import Tech from "@/slides/Tech";
import Traction from "@/slides/Traction";
import Closing from "@/slides/Closing";
import PresenterView from "@/deck/PresenterView";
import { CHANNEL, openPresenter } from "@/deck/sync";

const IS_PRESENTER = new URLSearchParams(window.location.search).has("presenter");

const SLIDES = [Cover, Problem, Solution, Product, Impact, Tech, Traction, Closing];

const variants = {
  enter: (d) => ({ opacity: 0, x: d * 90, scale: 0.985, filter: "blur(10px)" }),
  center: { opacity: 1, x: 0, scale: 1, filter: "blur(0px)" },
  exit: (d) => ({ opacity: 0, x: d * -90, scale: 0.985, filter: "blur(10px)" }),
};

function Deck() {
  const [[index, dir], setPage] = useState([0, 1]);
  const timer = useTimer(TOTAL_SECONDS);
  const touch = useRef(null);
  const chRef = useRef(null);
  const snap = useRef({});
  const { toggle, reset, elapsed, running } = timer;
  const [rehearse, setRehearse] = useState(false);
  const lastTarget = useRef(0);
  const toggleRehearse = useCallback(() => setRehearse((r) => !r), []);

  const step = useCallback((d) => {
    setPage(([i]) => [Math.max(0, Math.min(SLIDES.length - 1, i + d)), d]);
  }, []);

  const play = useCallback(() => {
    const s = snap.current;
    const fresh = !s.running && (!s.elapsed || s.elapsed >= TOTAL_SECONDS);
    if (!fresh) {
      toggle();
      return;
    }
    chRef.current?.postMessage({ type: "show-start" });
    if (s.rehearse) {
      lastTarget.current = 0;
      reset();
      setPage([0, -1]);
    } else setRehearse(true);
  }, [toggle, reset]);

  useEffect(() => {
    const ch = new BroadcastChannel(CHANNEL);
    chRef.current = ch;
    ch.onmessage = (e) => {
      const t = e.data?.type;
      if (t === "next") step(1);
      else if (t === "prev") step(-1);
      else if (t === "toggle") play();
      else if (t === "reset") reset();
      else if (t === "rehearse") toggleRehearse();
      else if (t === "first") setPage([0, -1]);
      else if (t === "hello") ch.postMessage(snap.current);
    };
    return () => ch.close();
  }, [step, play, reset, toggleRehearse]);

  useEffect(() => {
    snap.current = { type: "state", index, elapsed, running, rehearse };
    chRef.current?.postMessage(snap.current);
  }, [index, elapsed, running, rehearse]);

  const go = useCallback((n) => {
    setPage(([i]) => {
      const t = Math.max(0, Math.min(SLIDES.length - 1, n));
      return [t, t >= i ? 1 : -1];
    });
  }, []);

  useEffect(() => {
    if (!rehearse) return;
    lastTarget.current = 0;
    reset();
    go(0);
  }, [rehearse, reset, go]);

  useEffect(() => {
    if (!rehearse) return;
    const t = slideAt(elapsed);
    if (t !== lastTarget.current) {
      lastTarget.current = t;
      setPage((p) => (t > p[0] ? [t, 1] : p));
    }
  }, [rehearse, elapsed]);

  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (["ArrowRight", "ArrowDown", "PageDown", " "].includes(e.key)) {
        e.preventDefault();
        step(1);
      } else if (["ArrowLeft", "ArrowUp", "PageUp"].includes(e.key)) {
        e.preventDefault();
        step(-1);
      } else if (e.key === "p") play();
      else if (e.key === "r") reset();
      else if (e.key === "s") openPresenter();
      else if (e.key === "a") toggleRehearse();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [play, reset, step, toggleRehearse]);

  const onTouchStart = (e) => {
    touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onTouchEnd = (e) => {
    if (!touch.current) return;
    const dx = e.changedTouches[0].clientX - touch.current.x;
    const dy = e.changedTouches[0].clientY - touch.current.y;
    if (Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy) * 1.5) go(index + (dx < 0 ? 1 : -1));
    touch.current = null;
  };

  const Slide = SLIDES[index];

  return (
    <div className="deck-root" data-testid="pitch-deck" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="grain" aria-hidden />
      <TopBar timer={{ ...timer, toggle: play }} index={index} rehearse={rehearse} onRehearse={toggleRehearse} />
      <main className="fixed inset-0">
        <AnimatePresence initial={false} custom={dir}>
          <motion.section
            key={index}
            custom={dir}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.75, ease: EASE }}
            className="no-scrollbar absolute inset-0 overflow-y-auto overflow-x-hidden lg:overflow-hidden"
            data-testid={`slide-${index + 1}`}
          >
            <Slide go={go} />
          </motion.section>
        </AnimatePresence>
      </main>
      <DeckNav index={index} go={go} />
      <Toaster theme="dark" position="top-center" richColors />
    </div>
  );
}

function App() {
  return IS_PRESENTER ? <PresenterView /> : <Deck />;
}

export default App;
